import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Gauge, 
  Plus, 
  Calendar as CalendarIcon,
  Eye,
  Edit3,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Clock,
  MapPin,
  X,
  Shield
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, parseISO, isAfter, isBefore, addDays } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Equipment types for calibration
const CALIBRATION_TYPES = [
  'Pressure Gauge',
  'Flow Meter', 
  'Thermometer',
  'Level Gauge',
  'Temperature Controller',
  'Pressure Transmitter',
  'RTD Sensor',
  'Thermocouple',
  'pH Meter',
  'Conductivity Meter',
  'Gas Detector',
  'Safety Valve',
  'Control Valve',
  'Other'
];

// Calibration intervals
const CALIBRATION_INTERVALS = [
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
  { label: '6 Months', value: 180 },
  { label: '1 Year', value: 365 },
  { label: '2 Years', value: 730 },
];

function CalibrationPanel({ 
  isOpen, 
  onClose,
  currentSweep,
  cameraPosition,
  onNavigateToCalibration,
  title = "Compliance"
}) {
  const { token, canEdit, isViewer } = useAuth();
  const { currentTour } = useTour();
  
  const [calibrations, setCalibrations] = useState([]);
  const [equipment, setEquipment] = useState([]); // Existing equipment list
  const [loading, setLoading] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedCalibration, setSelectedCalibration] = useState(null);
  const [filter, setFilter] = useState('all'); // all, due, overdue, completed
  
  const [newCalibration, setNewCalibration] = useState({
    equipment_id: '', // Link to existing equipment
    equipment_name: '',
    equipment_type: '',
    tag_number: '',
    description: '',
    calibration_date: new Date(),
    calibration_interval_days: 365,
    notes: '',
    // Position inherited from equipment
    sweep_id: null,
    position: null,
    rotation: null
  });

  // Fetch calibrations for current tour
  const fetchCalibrations = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/calibrations/tour/${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCalibrations(data);
      }
    } catch (err) {
      console.error('Failed to fetch calibrations:', err);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  // Fetch equipment for current tour
  const fetchEquipment = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/equipment/tour/${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEquipment(data);
      }
    } catch (err) {
      console.error('Failed to fetch equipment:', err);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchCalibrations();
      fetchEquipment();
    }
  }, [isOpen, fetchCalibrations, fetchEquipment]);

  // When equipment is selected, auto-fill fields
  const handleEquipmentSelect = (equipmentId) => {
    const selectedEquip = equipment.find(e => e.id === equipmentId);
    if (selectedEquip) {
      setNewCalibration(prev => ({
        ...prev,
        equipment_id: equipmentId,
        equipment_name: selectedEquip.name,
        equipment_type: selectedEquip.equipment_type,
        tag_number: selectedEquip.tag_number || '',
        description: selectedEquip.description || '',
        sweep_id: selectedEquip.sweep_id || null,
        position: selectedEquip.position || null,
        rotation: selectedEquip.rotation || null
      }));
    }
  };

  // Get calibration status
  const getCalibrationStatus = (cal) => {
    if (cal.status === 'completed') return 'completed';
    
    const today = new Date();
    const dueDate = parseISO(cal.calibration_date);
    const warningDate = addDays(dueDate, -14); // 2 weeks warning
    
    if (isBefore(dueDate, today)) return 'overdue';
    if (isBefore(warningDate, today)) return 'due';
    return 'pending';
  };

  // Filter calibrations
  const filteredCalibrations = calibrations.filter(cal => {
    if (filter === 'all') return true;
    return getCalibrationStatus(cal) === filter;
  });

  // Create new calibration
  const handleCreateCalibration = async () => {
    if (!newCalibration.equipment_name || !newCalibration.equipment_type) {
      console.error('Missing required fields: equipment_name or equipment_type');
      alert('Please fill in Equipment Name and Equipment Type');
      return;
    }
    if (!token) {
      console.error('No auth token available');
      alert('Please log in to create calibrations');
      return;
    }
    if (!currentTour?.id) {
      console.error('No tour selected');
      alert('Please select a tour first');
      return;
    }

    try {
      const calibrationData = {
        tour_id: currentTour.id,
        equipment_name: newCalibration.equipment_name,
        equipment_type: newCalibration.equipment_type,
        tag_number: newCalibration.tag_number || null,
        description: newCalibration.description || null,
        calibration_date: format(newCalibration.calibration_date, 'yyyy-MM-dd'),
        calibration_interval_days: newCalibration.calibration_interval_days,
        notes: newCalibration.notes || null,
        status: 'pending',
        // Use position from linked equipment
        sweep_id: newCalibration.sweep_id || null,
        position: newCalibration.position || null,
        rotation: newCalibration.rotation || null
      };

      console.log('Creating calibration with data:', calibrationData);

      const res = await fetch(`${API_URL}/api/calibrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(calibrationData)
      });

      console.log('Create calibration response status:', res.status);

      if (res.ok) {
        const created = await res.json();
        console.log('Calibration created successfully:', created);
        await fetchCalibrations();
        setIsCreateDialogOpen(false);
        setNewCalibration({
          equipment_id: '',
          equipment_name: '',
          equipment_type: '',
          tag_number: '',
          description: '',
          calibration_date: new Date(),
          calibration_interval_days: 365,
          notes: '',
          sweep_id: null,
          position: null,
          rotation: null
        });
      } else {
        const errorText = await res.text();
        console.error('Failed to create calibration:', res.status, errorText);
        alert(`Failed to create calibration: ${errorText}`);
      }
    } catch (err) {
      console.error('Failed to create calibration:', err);
      alert(`Error creating calibration: ${err.message}`);
    }
  };

  // Mark calibration as complete
  const handleMarkComplete = async (calibrationId) => {
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/api/calibrations/${calibrationId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (res.ok) {
        await fetchCalibrations();
        setSelectedCalibration(null);
      }
    } catch (err) {
      console.error('Failed to complete calibration:', err);
    }
  };

  // Delete calibration
  const handleDelete = async (calibrationId) => {
    if (!token || !window.confirm('Delete this calibration item?')) return;

    try {
      const res = await fetch(`${API_URL}/api/calibrations/${calibrationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchCalibrations();
        setSelectedCalibration(null);
      }
    } catch (err) {
      console.error('Failed to delete calibration:', err);
    }
  };

  // Navigate to calibration location
  const handleNavigate = (cal) => {
    if (cal.sweep_id && onNavigateToCalibration) {
      setSelectedCalibration(null);
      setTimeout(() => {
        onNavigateToCalibration(cal.sweep_id, { 
          rotation: cal.rotation,
          transitionTime: 1500 
        });
      }, 100);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      pending: { label: 'Pending', variant: 'secondary', icon: Clock },
      due: { label: 'Due Soon', variant: 'warning', icon: AlertCircle },
      overdue: { label: 'Overdue', variant: 'destructive', icon: AlertCircle },
      completed: { label: 'Completed', variant: 'success', icon: CheckCircle2 }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // Count by status
  const statusCounts = {
    all: calibrations.length,
    pending: calibrations.filter(c => getCalibrationStatus(c) === 'pending').length,
    due: calibrations.filter(c => getCalibrationStatus(c) === 'due').length,
    overdue: calibrations.filter(c => getCalibrationStatus(c) === 'overdue').length,
    completed: calibrations.filter(c => getCalibrationStatus(c) === 'completed').length
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            {title}
          </SheetTitle>
          <SheetDescription>
            Track compliance, calibration dates and schedules
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-180px)] mt-4">
          {/* Header with Add button and filter */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1">
              {['all', 'due', 'overdue', 'pending', 'completed'].map(f => (
                <Button
                  key={f}
                  variant={filter === f ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs capitalize"
                  onClick={() => setFilter(f)}
                >
                  {f} ({statusCounts[f]})
                </Button>
              ))}
            </div>
            
            {canEdit && (
              <Button 
                size="sm" 
                className="gap-1"
                onClick={() => setIsCreateDialogOpen(true)}
                disabled={!currentTour?.id}
              >
                <Plus className="h-3 w-3" />
                Add
              </Button>
            )}
          </div>

          {/* Viewer notice */}
          {isViewer && (
            <div className="text-xs text-muted-foreground bg-secondary/50 p-2 rounded mb-4">
              View-only access. Contact an admin to manage calibrations.
            </div>
          )}

          {/* Calibration list */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : filteredCalibrations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Gauge className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No calibration items {filter !== 'all' ? `(${filter})` : ''}</p>
                {canEdit && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => setIsCreateDialogOpen(true)}
                  >
                    Add First Calibration
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredCalibrations.map(cal => (
                  <div 
                    key={cal.id}
                    className={cn(
                      "p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors",
                      getCalibrationStatus(cal) === 'overdue' && "border-destructive/50 bg-destructive/5",
                      getCalibrationStatus(cal) === 'due' && "border-warning/50 bg-warning/5"
                    )}
                    onClick={() => setSelectedCalibration(cal)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{cal.equipment_name}</span>
                          <StatusBadge status={getCalibrationStatus(cal)} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {cal.equipment_type}
                          {cal.tag_number && ` • ${cal.tag_number}`}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <CalendarIcon className="h-3 w-3" />
                          Due: {format(parseISO(cal.calibration_date), 'MMM d, yyyy')}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {cal.sweep_id && (
                          <Button 
                            variant="ghost" 
                            size="icon-sm"
                            onClick={(e) => { e.stopPropagation(); handleNavigate(cal); }}
                            title="Go to location"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Create Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Calibration Item</DialogTitle>
              <DialogDescription>
                Select equipment from your registry or enter manually
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Equipment Selection */}
              {equipment.length > 0 && (
                <div className="space-y-2">
                  <Label>Select from Equipment Registry</Label>
                  <Select
                    value={newCalibration.equipment_id}
                    onValueChange={handleEquipmentSelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose equipment..." />
                    </SelectTrigger>
                    <SelectContent>
                      {equipment.map(eq => (
                        <SelectItem key={eq.id} value={eq.id}>
                          {eq.name} {eq.tag_number && `(${eq.tag_number})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newCalibration.equipment_id && newCalibration.sweep_id && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      3D position linked from equipment
                    </p>
                  )}
                  <Separator className="my-2" />
                  <p className="text-xs text-muted-foreground">Or enter manually below:</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="equipment_name">Equipment Name *</Label>
                <Input
                  id="equipment_name"
                  placeholder="e.g., Pressure Gauge PG-001"
                  value={newCalibration.equipment_name}
                  onChange={(e) => setNewCalibration(prev => ({ ...prev, equipment_name: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Equipment Type *</Label>
                  <Select
                    value={newCalibration.equipment_type}
                    onValueChange={(v) => setNewCalibration(prev => ({ ...prev, equipment_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {CALIBRATION_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tag_number">Tag Number</Label>
                  <Input
                    id="tag_number"
                    placeholder="e.g., PG-001"
                    value={newCalibration.tag_number}
                    onChange={(e) => setNewCalibration(prev => ({ ...prev, tag_number: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Calibration Due Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(newCalibration.calibration_date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={newCalibration.calibration_date}
                      onSelect={(date) => date && setNewCalibration(prev => ({ ...prev, calibration_date: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Calibration Interval</Label>
                <Select
                  value={String(newCalibration.calibration_interval_days)}
                  onValueChange={(v) => setNewCalibration(prev => ({ ...prev, calibration_interval_days: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CALIBRATION_INTERVALS.map(int => (
                      <SelectItem key={int.value} value={String(int.value)}>{int.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Additional notes about calibration requirements..."
                  value={newCalibration.notes}
                  onChange={(e) => setNewCalibration(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateCalibration}
                disabled={!newCalibration.equipment_name || !newCalibration.equipment_type}
              >
                Add Calibration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Sheet */}
        {selectedCalibration && (
          <Dialog open={!!selectedCalibration} onOpenChange={() => setSelectedCalibration(null)}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedCalibration.equipment_name}
                  <StatusBadge status={getCalibrationStatus(selectedCalibration)} />
                </DialogTitle>
                <DialogDescription>
                  {selectedCalibration.equipment_type}
                  {selectedCalibration.tag_number && ` • ${selectedCalibration.tag_number}`}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-medium">{format(parseISO(selectedCalibration.calibration_date), 'PPP')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Interval</p>
                    <p className="font-medium">{selectedCalibration.calibration_interval_days} days</p>
                  </div>
                  {selectedCalibration.last_calibration_date && (
                    <div>
                      <p className="text-muted-foreground">Last Calibrated</p>
                      <p className="font-medium">{format(parseISO(selectedCalibration.last_calibration_date), 'PPP')}</p>
                    </div>
                  )}
                  {selectedCalibration.calibrated_by && (
                    <div>
                      <p className="text-muted-foreground">Calibrated By</p>
                      <p className="font-medium">{selectedCalibration.calibrated_by}</p>
                    </div>
                  )}
                </div>

                {selectedCalibration.description && (
                  <div>
                    <p className="text-sm text-muted-foreground">Description</p>
                    <p className="text-sm">{selectedCalibration.description}</p>
                  </div>
                )}

                {selectedCalibration.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground">Notes</p>
                    <p className="text-sm">{selectedCalibration.notes}</p>
                  </div>
                )}

                {selectedCalibration.sweep_id && (
                  <Button 
                    variant="outline" 
                    className="w-full gap-2"
                    onClick={() => handleNavigate(selectedCalibration)}
                  >
                    <Eye className="h-4 w-4" />
                    Go to Location
                  </Button>
                )}
              </div>

              {canEdit && (
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button 
                    variant="destructive" 
                    onClick={() => handleDelete(selectedCalibration.id)}
                    className="gap-1"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                  
                  {getCalibrationStatus(selectedCalibration) !== 'completed' && (
                    <Button 
                      variant="default"
                      onClick={() => handleMarkComplete(selectedCalibration.id)}
                      className="gap-1"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Mark Completed
                    </Button>
                  )}
                </DialogFooter>
              )}
            </DialogContent>
          </Dialog>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default CalibrationPanel;
